import { useState } from 'react'
import { useTranslation } from 'libs/i18n'
import { CubeIcon, PhotoIcon } from '@heroicons/react/24/outline'
import { NavLink, useParams } from 'libs/router'
import { Spinner } from 'shared/atoms'
import { EmptyState } from 'shared/components/EmptyState/EmptyState'
import { routes } from 'config/routes'
import { useFetchProducts } from './queries/useFetchProducts'
import { dashedEmptyClassName } from '../Sales/Sales'

const MAX_ROWS = 6

const ProductThumbnail = ({ src }: { src: string | null }) => {
	const [hasError, setHasError] = useState(false)

	if (src && !hasError) {
		return (
			<img
				src={src}
				alt=""
				className="h-10 w-10 shrink-0 rounded-md border border-border object-cover"
				onError={() => setHasError(true)}
			/>
		)
	}

	return (
		<span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-primary-soft text-primary">
			<PhotoIcon className="h-4 w-4 stroke-2" />
		</span>
	)
}

export const ProductsTable = () => {
	const { t } = useTranslation()
	const params = useParams()
	const bookingId = params.id

	const { products, isProductsLoading } = useFetchProducts({ bookingId })
	const results = products?.data?.results ?? []

	return (
		<section className="rounded-lg border border-border bg-card p-5 shadow-panel sm:p-6">
			<div className="flex items-center justify-between gap-3">
				<h2 className="font-display text-base font-semibold text-foreground">{t('Products')}</h2>
				{results.length > 0 && (
					<NavLink
						className="text-[12px] font-semibold text-primary hover:underline"
						to={`${routes.productIndex}?booking_id=${bookingId}`}>
						{t('View all')}
					</NavLink>
				)}
			</div>
			{isProductsLoading ? (
				<div className="flex justify-center py-8">
					<Spinner />
				</div>
			) : results.length ? (
				<ul className="mt-4 space-y-2">
					{results.slice(0, MAX_ROWS).map((product) => (
						<li key={product.id}>
							<NavLink
								className="flex items-center gap-3 rounded-md border border-border p-3 transition-colors hover:bg-muted/40"
								to={routes.productEdit(String(product.id))}>
								<ProductThumbnail src={product.image_thumbnail} />
								<span className="min-w-0">
									<span className="block truncate text-[13px] font-semibold text-foreground">
										{product.name}
									</span>
									<span className="block text-[11px] text-muted-foreground">{product.sku}</span>
								</span>
							</NavLink>
						</li>
					))}
				</ul>
			) : (
				<EmptyState
					className={dashedEmptyClassName}
					icon={<CubeIcon />}
					text={t('No products yet')}
					description={t('Products added to this booking will appear here.')}
				/>
			)}
		</section>
	)
}
